/**
 * «Користувачі» (/admin/users) — РЕДИЗАЙН (VITE_REDESIGN).
 *
 * Прод без прапорця бачить незмінений TenantUsersPageLegacy у page.tsx:
 * маршрут не гейтований жодним VITE_FEATURE_*, тож у прод-збірці сторінка є.
 *
 * Головне, що виправлено — тут тема була зламана ПОВНІСТЮ, а не місцями:
 * компонент оголошував десять змінних через isDark (cardBg, textMain,
 * tableHeaderBg …) і не використовував ЖОДНОЇ, а весь рендер був прибитий
 * світлими літералами (#fff, #E5E5E8, #1A1A1A, #6B6B70, #F5F5F7). У темній
 * темі сторінка лишалась білою.
 *
 * Решта:
 *  · увесь інтерфейс був хардкодом англійською в україномовному кабінеті —
 *    переведено на i18n (users_* / role_*), топбар показує «Користувачі»;
 *  · кольори ролей поза догмою (owner var(--accent) — червоний як декор, §3);
 *  · роль редагувалась нативним <select> у вигляді pill — некерований список,
 *    у темній темі світлий попап; тепер TokenSelect;
 *  · ліва колонка тенантів → TokenSelect у топбарі (§6, useUsersUi);
 *  · Inter на h1, 7 Remix-іконок, font-mono на slug — прибрано (§0).
 *
 * ЛОГІКА НЕ ЗМІНЕНА: ті самі запити, той самий PATCH ролі з оптимістичним
 * оновленням стану, ті самі тости й перелік ROLES.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useUsersUi } from '@/hooks/useUsersUi';
import TokenSelect from '@/components/base/TokenSelect';
import { Building2, RefreshCw, Users } from 'lucide-react';

const ROLES = ['owner', 'admin', 'editor', 'operator', 'finance', 'viewer'] as const;
type Role = typeof ROLES[number];

interface Tenant { id: string; name: string; slug: string; status: string }

interface TenantUser {
  id: string;
  tenantId: string;
  userId: string;
  role: Role;
  createdAt: string;
  updatedAt: string;
  user: { id: string; fullName: string; email: string; externalAuthId?: string };
  tenant?: { id: string; name: string; slug: string };
}

/**
 * Ролі за догмою §3: оператор → --ok (діюча роль), адміністратор → --warn
 * (підвищені права — привід звернути увагу), власник → --series-3 (брендовий,
 * але не тривожний), решта нейтральні. Червоного немає: роль не є помилкою.
 */
const ROLE_COLOR: Record<Role, string> = {
  owner: 'var(--series-3)',
  admin: 'var(--warn)',
  operator: 'var(--ok)',
  editor: 'var(--series-2)',
  finance: 'var(--text-dim)',
  viewer: 'var(--text-dim)',
};

export default function UsersRedesign() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const ui = useUsersUi();
  const { tenantId, search, setTenantId, setTenants: pushTenants } = ui;

  const [tenants, setTenantsLocal] = useState<Tenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [changingRole, setChangingRole] = useState<string | null>(null);

  // Тенанти — один раз на монтуванні; перший стає обраним, як і раніше.
  useEffect(() => {
    const loadTenants = async () => {
      setTenantsLoading(true);
      try {
        const data = await api.get<Tenant[]>('/tenants');
        setTenantsLocal(data);
        pushTenants(data.map((x) => ({ id: x.id, name: x.name })));
        if (data.length > 0 && !tenantId) setTenantId(data[0].id);
      } catch (err) {
        addToast(getApiErrorMessage(err, 'Failed to load tenants'), 'error');
      } finally {
        setTenantsLoading(false);
      }
    };
    loadTenants();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedTenant = useMemo(
    () => tenants.find((x) => x.id === tenantId) ?? null,
    [tenants, tenantId],
  );

  const loadUsers = useCallback(async (id: string) => {
    setUsersLoading(true);
    setUsers([]);
    try {
      const data = await api.get<TenantUser[]>(`/tenants/${id}/users`);
      setUsers(data);
    } catch (err) {
      addToast(getApiErrorMessage(err, 'Failed to load users'), 'error');
    } finally {
      setUsersLoading(false);
      setUsersLoaded(true);
    }
  }, [addToast]);

  useEffect(() => { if (tenantId) loadUsers(tenantId); }, [tenantId, loadUsers]);

  // PATCH ролі — код збережено дослівно, включно з оптимістичним оновленням.
  const handleRoleChange = async (tenantUserId: string, newRole: Role) => {
    setChangingRole(tenantUserId);
    try {
      await api.patch(`/tenant-users/${tenantUserId}/role`, { role: newRole });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === tenantUserId ? { ...u, role: newRole, updatedAt: new Date().toISOString() } : u,
        ),
      );
      addToast(t('users_role_updated'), 'success');
    } catch (err) {
      addToast(getApiErrorMessage(err, 'Failed to update role'), 'error');
    } finally {
      setChangingRole(null);
    }
  };

  const filtered = useMemo(() => users.filter(
    (u) =>
      (u.user.fullName ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (u.user.email ?? '').toLowerCase().includes(search.toLowerCase()),
  ), [users, search]);

  const roleLabel = (r: string) => t(`role_${r}`, { defaultValue: r });

  const th: any = {
    textAlign: 'left', padding: '10px 14px', fontSize: 10.5, fontWeight: 700,
    letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)',
    background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap',
  };
  const td: any = {
    padding: '10px 14px', fontSize: 12.5, color: 'var(--text)',
    borderBottom: '1px solid var(--line)',
  };

  const firstLoad = usersLoading && !usersLoaded;

  return (
    <DashboardLayout breadcrumb={[t('nav_set_users')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {/* Заголовок у тілі не дублюємо: назву розділу показує топбар.
              Лишається лише функціональна кнопка оновлення. */}
          <div className="mb-3 flex justify-end">
            <button onClick={() => tenantId && loadUsers(tenantId)}
              disabled={!tenantId || usersLoading} title={t('users_loading')}
              className="inline-flex items-center justify-center cursor-pointer shrink-0"
              style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--surface-2)',
                       border: '1px solid var(--line)', color: 'var(--text-muted)',
                       opacity: (!tenantId || usersLoading) ? 0.55 : 1 }}>
              <RefreshCw size={15} strokeWidth={2} className={usersLoading ? 'animate-spin' : undefined} />
            </button>
          </div>

          <div className="tile overflow-hidden">
            {tenantsLoading ? (
              <div className="p-12 text-center">
                <RefreshCw size={22} strokeWidth={2} className="animate-spin mx-auto" style={{ color: 'var(--text-muted)' }} />
              </div>
            ) : tenants.length === 0 ? (
              <div className="p-12 text-center">
                <Building2 size={22} strokeWidth={1.8} className="mx-auto mb-3" style={{ color: 'var(--text-dim)' }} />
                <p className="text-[13px] font-medium" style={{ color: 'var(--text)' }}>{t('users_no_tenants')}</p>
              </div>
            ) : !selectedTenant ? (
              <div className="p-12 text-center">
                <Building2 size={22} strokeWidth={1.8} className="mx-auto mb-3" style={{ color: 'var(--text-dim)' }} />
                <p className="text-[13px] font-medium" style={{ color: 'var(--text)' }}>{t('users_select_tenant')}</p>
                <p className="text-[11.5px] mt-1" style={{ color: 'var(--text-muted)' }}>{t('users_select_tenant_hint')}</p>
              </div>
            ) : firstLoad ? (
              <div className="p-12 text-center">
                <RefreshCw size={22} strokeWidth={2} className="animate-spin mx-auto" style={{ color: 'var(--text-muted)' }} />
                <p className="text-[12.5px] mt-3" style={{ color: 'var(--text-muted)' }}>{t('users_loading')}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <Users size={22} strokeWidth={1.8} className="mx-auto mb-3" style={{ color: 'var(--text-dim)' }} />
                <p className="text-[13px] font-medium" style={{ color: 'var(--text)' }}>{t('users_not_found')}</p>
                <p className="text-[11.5px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {search ? t('users_filter_hint') : t('users_not_found_hint')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>{t('users_col_user')}</th>
                      <th style={th}>{t('users_col_email')}</th>
                      <th style={{ ...th, width: 190 }}>{t('users_col_role')}</th>
                      <th style={th}>{t('users_col_added')}</th>
                      <th style={th}>{t('users_col_updated')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((tu) => {
                      const isChanging = changingRole === tu.id;
                      const rc = ROLE_COLOR[tu.role] ?? 'var(--text-dim)';
                      return (
                        <tr key={tu.id} className="transition-colors"
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                          <td style={td}>
                            <div className="flex items-center gap-2.5">
                              {/* Кружок ініціалів — нейтральний: колір тут нічого не означає. */}
                              <div className="flex items-center justify-center text-[11px] font-bold shrink-0"
                                style={{ width: 28, height: 28, borderRadius: 999,
                                         background: 'var(--surface-2)', border: '1px solid var(--line)',
                                         color: 'var(--text-muted)' }}>
                                {(tu.user.fullName || '?').slice(0, 1).toUpperCase()}
                              </div>
                              <span className="font-medium">{tu.user.fullName}</span>
                            </div>
                          </td>
                          <td style={{ ...td, color: 'var(--text-muted)', fontSize: 11.5 }}>{tu.user.email}</td>
                          <td style={td}>
                            <div className="flex items-center gap-2">
                              {isChanging && (
                                <RefreshCw size={13} strokeWidth={2} className="animate-spin shrink-0" style={{ color: 'var(--text-muted)' }} />
                              )}
                              <TokenSelect
                                value={tu.role} disabled={isChanging} width={150}
                                ariaLabel={t('users_col_role')}
                                onChange={(v) => handleRoleChange(tu.id, v as Role)}
                                triggerStyle={{ color: rc, borderColor: rc,
                                                background: `color-mix(in srgb, ${rc} 10%, transparent)` }}
                                options={ROLES.map((r) => ({ value: r, label: roleLabel(r) }))} />
                            </div>
                          </td>
                          <td style={{ ...td, color: 'var(--text-muted)', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                            {tu.createdAt ? new Date(tu.createdAt).toLocaleDateString('uk-UA') : '—'}
                          </td>
                          <td style={{ ...td, color: 'var(--text-muted)', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                            {tu.updatedAt ? new Date(tu.updatedAt).toLocaleDateString('uk-UA') : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selectedTenant && !firstLoad && filtered.length > 0 && (
            <div className="text-[11px] mt-2" style={{ color: 'var(--text-dim)' }}>
              {selectedTenant.name} · {selectedTenant.slug} · показано {filtered.length} з {users.length}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
